import _ from "lodash";
import React, { ReactElement, useEffect, useState } from "react";
import {
	Button,
	Card,
	Col,
	Container,
	FormGroup,
	FormLabel,
	Row,
	Table,
} from "react-bootstrap";

import { FormControl } from "react-bootstrap";
import { NumericFormat } from "react-number-format";
import { RouteComponentProps } from "react-router";
import { OpportunitesForAccountSelect, Opportunity } from "../opportunities";
import { Editable, Loading } from "../ui";
import { Project } from "./Project";
import { useProjectBudgetContext } from "./ProjectBudgetContext";
import { ProjectBudgetReportCard } from "./ProjectBudgetReportCard";
import { ProjectsForAccountSelect } from "./ProjectsForAccountSelect";

interface IParams {
	id?: string;
}

export const ProjectBudgetPage = (
	props: RouteComponentProps<IParams>,
): ReactElement => {
	const projectBudgetContext = useProjectBudgetContext();

	const [showAddProject, setShowAddProject] = useState(false);
	const [addProject, setAddProject] = useState<Project | null>();

	useEffect(() => {
		projectBudgetContext.fetchProjectBudget!(
			parseInt(props.match.params.id || "", 10),
		);

		return function cleanup() {
			projectBudgetContext.setBudget!(null);
		};
	}, []);

	if (!projectBudgetContext.budget) {
		return <Loading />;
	}

	const budget = projectBudgetContext.budget;

	const titleField = (
		<Editable
			shownComponent={<h1>{budget.name}</h1>}
			editComponent={
				<FormGroup>
					<FormControl
						type="text"
						defaultValue={budget.name}
						onChange={(event: any) => {
							budget.name = event.target.value;
						}}
						style={{
							fontSize: "2.5rem",
							marginTop: -12,
							marginLeft: -12,
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				projectBudgetContext.save!();
			}}
		/>
	);
	const notesField = (
		<Editable
			shownComponent={<p>{budget.notes}</p>}
			editComponent={
				<FormGroup>
					<FormControl
						type="text"
						defaultValue={budget.notes}
						onChange={(event: any) => {
							budget.notes = event.target.value;
						}}
						style={{
							marginLeft: -12,
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				projectBudgetContext.save!();
			}}
		/>
	);

	const budgetField = (
		<Editable
			shownComponent={
				<NumericFormat
					value={budget.amount}
					displayType={"text"}
					thousandSeparator={true}
					prefix={"$"}
				/>
			}
			editComponent={
				<FormGroup>
					<FormControl
						type="text"
						defaultValue={String(budget.amount)}
						onChange={(event: any) => {
							budget.amount = event.target.value;
						}}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				projectBudgetContext.save!();
			}}
		/>
	);

	// Amounts are stored in USD, so a CAD report converts before dividing by the
	// hourly rate. Mirrors ProjectBudget::toHours() on the server.
	const projectHours = (amount: number): number | null => {
		if (!budget.hourlyRate || budget.hourlyRate <= 0) {
			return null;
		}

		const rate =
			budget.reportCurrency === "CAD" ? Number(budget.exchangeRate) : 1;

		if (!rate || rate <= 0) {
			return null;
		}

		return (Number(amount) * rate) / Number(budget.hourlyRate);
	};

	// The dollar amount that would make a project's budgeted hours match the
	// hours actually logged against it - the inverse of projectHours(). Rounded
	// up to the cent so the recalculated hours never land a hair short of the
	// logged total and leave the row still flagged.
	const projectAmountForHours = (hours: number): number | null => {
		if (!budget.hourlyRate || budget.hourlyRate <= 0) {
			return null;
		}

		const rate =
			budget.reportCurrency === "CAD" ? Number(budget.exchangeRate) : 1;

		if (!rate || rate <= 0) {
			return null;
		}

		return (
			Math.ceil((hours * Number(budget.hourlyRate) * 100) / rate) / 100
		);
	};

	// Projects picked from the add form have not been through the API yet, so
	// they carry no logged hours.
	const loggedHours = (proj: Project): number =>
		Number(proj.hoursLogged) || 0;

	// Both figures are shown to two decimals, so they are compared there too -
	// anything smaller is a rounding artefact, not time to bill for.
	const isOverLogged = (proj: Project): boolean => {
		const budgeted = projectHours(proj.pivot.amount);

		if (budgeted === null) {
			return false;
		}

		return Math.round(loggedHours(proj) * 100) > Math.round(budgeted * 100);
	};

	// Raise the allocated amount so the project's budgeted hours cover the time
	// already logged against it. This can take the budget's available amount
	// negative, which is the honest position to be in.
	const matchProjectToLoggedHours = (proj: Project) => {
		const amount = projectAmountForHours(loggedHours(proj));

		if (amount === null) {
			return;
		}

		proj.pivot.amount = amount;
		projectBudgetContext.save!();
	};

	// Hours are only meaningful once an hourly rate has been set on the budget.
	const hoursLine = (hours: number | null) =>
		hours === null || hours === undefined ? null : (
			<div>
				<small>
					<NumericFormat
						value={hours}
						displayType={"text"}
						decimalScale={2}
						fixedDecimalScale
						thousandSeparator={true}
						suffix={" hrs"}
					/>
				</small>
			</div>
		);

	const OpportunityField = (
		<Editable
			shownComponent={
				<Table bordered striped>
					<thead>
						<tr>
							<th>#</th>
							<th>Name</th>
							<th>Amount</th>
							<th>Stage</th>
						</tr>
					</thead>
					<tbody>
						{budget.opportunities.map((opp, index) => (
							<tr key={index}>
								<td>{opp.id}</td>
								<td>
									<a href={`/opportunities/${opp.id}`}>
										{opp.name}
									</a>
								</td>
								<td>
									<NumericFormat
										value={opp.amount}
										displayType={"text"}
										decimalScale={2}
										fixedDecimalScale
										thousandSeparator={true}
										prefix={"$"}
									/>
								</td>
								<td>{opp.stage}</td>
							</tr>
						))}
					</tbody>
				</Table>
			}
			editComponent={
				<FormGroup>
					<OpportunitesForAccountSelect
						accountId={String(budget.accountId)}
						budgetId={budget.id}
						isMulti
						returnModels
						handleChange={(selected, _type) => {
							budget.opportunities = selected;
							projectBudgetContext.save!();
						}}
						defaultValue={
							budget.opportunities
								? budget.opportunities.map(
										(opportunity: Opportunity) => {
											return {
												value: opportunity.id,
												label: opportunity.name,
											};
										},
								  )
								: null
						}
					/>
				</FormGroup>
			}
			onFinishedEditing={() => {
				return;
			}}
		/>
	);

	const removeProject = (projectId: number | null) => {
		if (!projectId) {
			return;
		}

		_.remove(budget.projects, o => {
			return o.id === projectId;
		});

		projectBudgetContext.save!();
	};

	const saveNewProject = () => {
		if (!addProject) {
			return;
		}
		budget.projects.push(addProject);
		projectBudgetContext.save!();

		setAddProject(null);
		setShowAddProject(false);
	};

	const projectsField = (
		<Table bordered striped>
			<thead>
				<tr>
					<th>#</th>
					<th>Name</th>
					<th>Amount Used</th>
					<th>Hours</th>
					<th>Logged</th>
					<th>Notes</th>
					<th>Status</th>
					<th>Actions</th>
				</tr>
			</thead>
			<tbody>
				{budget.projects.map((proj, index) => (
					<tr key={index}>
						<td>{proj.id}</td>
						<td>
							<a href={`/projects/${proj.id}`}>{proj.name}</a>
						</td>
						<td>
							<Editable
								shownComponent={
									<NumericFormat
										value={proj.pivot.amount}
										displayType={"text"}
										thousandSeparator={true}
										prefix={"$"}
									/>
								}
								editComponent={
									<FormGroup>
										<FormControl
											type="text"
											defaultValue={String(
												proj.pivot.amount,
											)}
											onChange={(event: any) => {
												proj.pivot.amount =
													event.target.value;
											}}
										/>
									</FormGroup>
								}
								onFinishedEditing={() => {
									projectBudgetContext.save!();
								}}
							/>
						</td>
						<td>
							{projectHours(proj.pivot.amount) !== null ? (
								<NumericFormat
									value={projectHours(proj.pivot.amount)!}
									displayType={"text"}
									decimalScale={2}
									fixedDecimalScale
									thousandSeparator={true}
								/>
							) : (
								<span className="text-muted">&mdash;</span>
							)}
						</td>
						<td
							className={
								isOverLogged(proj) ? "text-danger" : undefined
							}
						>
							<NumericFormat
								value={loggedHours(proj)}
								displayType={"text"}
								decimalScale={2}
								fixedDecimalScale
								thousandSeparator={true}
							/>
							{isOverLogged(proj) ? (
								<Button
									variant="link"
									size="sm"
									className="p-0 d-block"
									title={`Allocate $${projectAmountForHours(
										loggedHours(proj),
									)!.toLocaleString(undefined, {
										minimumFractionDigits: 2,
										maximumFractionDigits: 2,
									})} to this project so its hours cover the time logged`}
									onClick={() =>
										matchProjectToLoggedHours(proj)
									}
								>
									Update
								</Button>
							) : null}
						</td>
						<td>
							<Editable
								shownComponent={proj.pivot.notes}
								editComponent={
									<FormGroup>
										<FormControl
											type="text"
											defaultValue={proj.pivot.notes}
											onChange={(event: any) => {
												proj.pivot.notes =
													event.target.value;
											}}
										/>
									</FormGroup>
								}
								onFinishedEditing={() => {
									projectBudgetContext.save!();
								}}
							/>
						</td>
						<td>{proj.status}</td>
						<td>
							<Button
								variant="danger"
								onClick={() => removeProject(proj.id)}
							>
								Remove
							</Button>
						</td>
					</tr>
				))}
			</tbody>
		</Table>
	);

	const addProjectFields = (
		<>
			<FormGroup>
				<ProjectsForAccountSelect
					accountId={String(budget.accountId)}
					returnModels
					handleChange={(selected, _type) => {
						selected.pivot = {
							amount: 0,
							notes: "",
						};
						setAddProject(selected);
					}}
					defaultValue={null}
				/>
			</FormGroup>
			{addProject ? (
				<>
					<FormGroup>
						<FormGroup>
							<FormLabel>Amount to allocate</FormLabel>
							<FormControl
								type="text"
								value={addProject.pivot.amount}
								onChange={(event: any) => {
									addProject.pivot.amount =
										event.target.value;
									setAddProject(_.cloneDeep(addProject));
								}}
							/>
						</FormGroup>
					</FormGroup>
					<FormGroup>
						<FormGroup>
							<FormLabel>Notes</FormLabel>
							<FormControl
								type="text"
								defaultValue={
									addProject ? addProject.pivot.notes : ""
								}
								onChange={(event: any) => {
									addProject.pivot.notes = event.target.value;
									setAddProject(_.cloneDeep(addProject));
								}}
							/>
						</FormGroup>
					</FormGroup>

					<Button className="mr-2" onClick={saveNewProject}>
						Allocate budget
					</Button>
					<Button
						variant="secondary"
						onClick={() => {
							setShowAddProject(false);
							setAddProject(null);
						}}
					>
						Cancel
					</Button>
				</>
			) : null}
		</>
	);

	return (
		<div id="project-dashboard">
			<Container className="project-details-container" fluid>
				<div className="title-wrapper">{titleField}</div>
				<div>{notesField}</div>
				<Row>
					<Col>
						<Row className="project-details">
							<Col sm={2} className="mb-4">
								<Card
									className="mb-3"
									style={{ height: "auto" }}
								>
									<Card.Body>
										<Card.Title>Budget</Card.Title>
										{budgetField}
										<div>
											<small>
												FPSC:{" "}
												<NumericFormat
													value={budget.amount * 1.28}
													decimalScale={2}
													displayType={"text"}
													thousandSeparator={true}
													prefix={"$"}
												/>
											</small>
										</div>
										{hoursLine(budget.hoursTotal)}
									</Card.Body>
								</Card>
								<Card
									className="mb-3"
									style={{ height: "auto" }}
								>
									<Card.Body>
										<Card.Title>Amount Invoiced</Card.Title>
										<NumericFormat
											value={budget.amountPaid}
											displayType={"text"}
											thousandSeparator={true}
											prefix={"$"}
										/>
									</Card.Body>
								</Card>
								<Card
									style={{ height: "auto" }}
									className="mb-3"
								>
									<Card.Body>
										<Card.Title>
											Amount to be Invoiced
										</Card.Title>
										<NumericFormat
											value={budget.amountPending}
											displayType={"text"}
											thousandSeparator={true}
											prefix={"$"}
										/>
									</Card.Body>
								</Card>
								<Card
									style={{ height: "auto" }}
									className="mb-3"
								>
									<Card.Body>
										<Card.Title>Amount Used</Card.Title>
										<NumericFormat
											value={budget.amountAllocated}
											displayType={"text"}
											decimalScale={2}
											thousandSeparator={true}
											prefix={"$"}
										/>
										{hoursLine(budget.hoursUsed)}
									</Card.Body>
								</Card>
								<Card style={{ height: "auto" }}>
									<Card.Body>
										<Card.Title>
											Amount Available
										</Card.Title>
										<NumericFormat
											value={budget.amountAvailable}
											decimalScale={2}
											displayType={"text"}
											thousandSeparator={true}
											prefix={"$"}
										/>
										<div>
											<small>
												FPSC:{" "}
												<NumericFormat
													value={
														budget.amountAvailable *
														1.28
													}
													decimalScale={2}
													displayType={"text"}
													thousandSeparator={true}
													prefix={"$"}
												/>
											</small>
										</div>
										{hoursLine(budget.hoursAvailable)}
									</Card.Body>
								</Card>
							</Col>
							<Col sm={4} className="mb-4">
								<Card>
									<Card.Body>
										<Card.Title>Opportunities</Card.Title>
										<p>
											Opportunites with the intention of
											paying towards this budget should be
											linked here. Any Closed - Won
											opportuninity will be added to the
											total amount invoiced.
										</p>
										{OpportunityField}
									</Card.Body>
								</Card>
							</Col>
							<Col sm={6} className="mb-4">
								<Card>
									<Card.Body>
										<Card.Title>
											Projects
											<Button
												variant="link"
												className="float-right"
												onClick={() => {
													setShowAddProject(true);
												}}
											>
												+ Add Project
											</Button>
										</Card.Title>
										<p />
										{showAddProject
											? addProjectFields
											: projectsField}
									</Card.Body>
								</Card>
							</Col>
						</Row>
						<Row>
							<Col sm={{ span: 10, offset: 2 }}>
								<ProjectBudgetReportCard />
							</Col>
						</Row>
					</Col>
				</Row>
			</Container>
		</div>
	);
};
